import { useState, useEffect, useCallback } from "react";
import { useThemeColor } from "@/hooks/useThemeColor";

const MESSAGES = [
  { icon: "ri-coupon-3-line", text: "كود خصم خاص للعملاء الجدد: Ebdaa-taif (خصم 10%)", highlight: "Ebdaa-taif" },
  { icon: "ri-truck-line", text: "شحن مجاني لجميع الطلبات داخل المملكة", highlight: "شحن مجاني" },
  { icon: "ri-refresh-line", text: "إرجاع واسترداد مجاني خلال 7 أيام من الاستلام", highlight: "إرجاع مجاني" },
  { icon: "ri-flashlight-line", text: "توصيل سريع خلال 24 ساعة في الرياض وجدة", highlight: "توصيل سريع" },
];

const STORAGE_KEY = "ebdaa_bar_closed";

interface AnnouncementBarProps {
  onVisibilityChange: (visible: boolean) => void;
}

export default function AnnouncementBar({ onVisibilityChange }: AnnouncementBarProps) {
  const [visible, setVisible] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) !== "1"; } catch { return true; }
  });
  const [activeIdx, setActiveIdx] = useState(0);
  const [animating, setAnimating] = useState(false);
  const tc = useThemeColor();

  // Notify parent of visibility
  useEffect(() => {
    onVisibilityChange(visible);
  }, [visible, onVisibilityChange]);

  // Auto-rotate messages
  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setActiveIdx((i) => (i + 1) % MESSAGES.length);
        setAnimating(false);
      }, 300);
    }, 3500);
    return () => clearInterval(interval);
  }, [visible]);

  const handleClose = useCallback(() => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
  }, []);

  if (!visible) return null;

  const msg = MESSAGES[activeIdx];

  return (
    <div
      dir="rtl"
      className="fixed top-0 left-0 right-0 z-[60] h-10 flex items-center justify-between px-4 overflow-hidden"
      style={{ background: tc.primary }}
    >
      {/* Dots */}
      <div className="hidden md:flex items-center gap-3 flex-shrink-0">
        {MESSAGES.map((_, i) => (
          <button
            key={i}
            onClick={() => { setAnimating(true); setTimeout(() => { setActiveIdx(i); setAnimating(false); }, 200); }}
            className={`h-1.5 rounded-full transition-all cursor-pointer`}
            style={{
              width: i === activeIdx ? '16px' : '6px',
              background: i === activeIdx ? tc.textOnPrimary : `${tc.textOnPrimary}4d`,
            }}
          />
        ))}
      </div>

      {/* Message */}
      <div className="flex-1 flex items-center justify-center gap-2 overflow-hidden">
        <div
          className="flex items-center gap-2 transition-all duration-300"
          style={{ opacity: animating ? 0 : 1, transform: animating ? "translateY(-6px)" : "translateY(0)" }}
        >
          <i className={`${msg.icon} text-sm flex-shrink-0`} style={{ color: tc.textOnPrimary }}></i>
          <p className="text-xs md:text-sm font-bold whitespace-nowrap" style={{ fontFamily: "'Cairo', sans-serif", color: tc.textOnPrimary }}>
            {msg.text.split(msg.highlight).map((part, i, arr) => (
              i < arr.length - 1 ? (
                <span key={i}>
                  {part}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(msg.highlight);
                      const btn = e.currentTarget;
                      btn.style.backgroundColor = "rgba(0,0,0,0.2)";
                      setTimeout(() => btn.style.backgroundColor = "", 200);
                    }}
                    className="underline underline-offset-2 hover:bg-black/10 px-1 rounded cursor-pointer transition-colors"
                    style={{ textDecorationColor: `${tc.textOnPrimary}80`, color: tc.textOnPrimary }}
                    title="انقر للنسخ"
                  >
                    {msg.highlight}
                  </button>
                </span>
              ) : part
            ))}
          </p>
        </div>
      </div>

      {/* Close */}
      <button
        onClick={handleClose}
        className="w-7 h-7 flex items-center justify-center hover:bg-black/10 rounded-full transition-all cursor-pointer flex-shrink-0"
        style={{ color: `${tc.textOnPrimary}99` }}
        aria-label="إغلاق"
      >
        <i className="ri-close-line text-base"></i>
      </button>
    </div>
  );
}
